import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../store/store';
import { FonctionTypeUserEnum } from '../../../utils/userFunctionsMap';

const STORAGE_KEY = 'rgpdcopro:assignedCoprosOnly';

const getStoredPreference = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const persistPreference = (value: boolean) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // ignore persistence errors (private browsing, etc.)
  }
};

type CoproAffectationToggleProps = {
  onFilterChange: (assignedCoprosOnly: boolean) => void;
};

function CoproAffectationToggle({ onFilterChange }: CoproAffectationToggleProps) {
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const fonctionType = useSelector((state: RootState) => state.auth.fonction?.type);

  const isSeeAllUser =
    isAdmin ||
    fonctionType === FonctionTypeUserEnum.RGPD_REFERANT ||
    fonctionType === FonctionTypeUserEnum.REPRESENTANT_LEGAL;

  const [assignedCoprosOnly, setAssignedCoprosOnly] = useState(getStoredPreference);

  useEffect(() => {
    if (isSeeAllUser) {
      onFilterChange(assignedCoprosOnly);
    }
  }, []);

  const handleChange = useCallback(
    (value: boolean) => {
      setAssignedCoprosOnly(value);
      persistPreference(value);
      onFilterChange(value);
    },
    [onFilterChange],
  );

  if (!isSeeAllUser) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-500">Périmètre :</span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => handleChange(false)}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 ${
            !assignedCoprosOnly
              ? 'bg-rgpd-blue text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Toutes les copros
        </button>
        <button
          type="button"
          onClick={() => handleChange(true)}
          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 ${
            assignedCoprosOnly
              ? 'bg-rgpd-blue text-white shadow-sm'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          Mes copros
        </button>
      </div>
    </div>
  );
}

export { CoproAffectationToggle, getStoredPreference };
